/**
 * BL-75 (D-210) · Touch-first placement — port factories.
 *
 * These EXTRACT (do not rewrite) the existing `setupInputHandlers` closures so the
 * DOM-agnostic `PlacementController` and the legacy mouse path share ONE implementation:
 *  - `createPreviewPort` ← `updateHoverPreview` / `clearHoverPreview` (the debounced reroute A*)
 *  - `createPlacePort`   ← the `click` handler's firewall + tower/signal commit bodies
 *
 * Because the bodies are byte-for-byte the same operations in the same order, the headless
 * `sim-fullrun-baseline` (which never drives input) stays byte-identical.
 */
import type { Vec2 } from "../core/types.ts";
import { CellType } from "../core/types.ts";
import { CELL_SIZE } from "../core/config.ts";
import { Tower } from "../entities/Tower.ts";
import { Firewall, FIREWALL_DEFAULTS } from "../entities/Firewall.ts";
import { rerouteAllPaths } from "../systems/PathRerouter.ts";
import type { Grid } from "../core/Grid.ts";
import type { EventBus } from "../core/EventBus.ts";
import type { EconomySystem } from "../systems/EconomySystem.ts";
import type { SynergySystem } from "../systems/SynergySystem.ts";
import type { WaveManager } from "../systems/WaveManager.ts";
import type { PathRegistry } from "../core/PathRegistry.ts";
import type { PlaceableDescriptor, PlacePort, PreviewPort } from "./placementTypes.ts";

/** Minimal shape of the shared aim-state the preview writes into (the renderer's `mouse`). */
interface PreviewTarget {
  previewPaths: Vec2[][] | null;
}

interface PreviewPathSource {
  previewAllPixelPathsWithBlock(col: number, row: number): Vec2[][];
}

/**
 * The hover-preview port — recompute the what-if reroute only when the snapped cell
 * changes (≈1 A* PER ACTIVE PATH per distinct cell, not per pixel). Pure display, no
 * sim mutation.
 *
 * LD-22.3 (Bug: 2nd-spawn preview missing): this now previews EVERY registered path
 * (primary + breach secondaries), not just "primary". Committing a tower reroutes all
 * paths (rerouteAllPaths), so the hover preview must mirror that or secondary spawns
 * show no route change until after the tower lands. The renderer already iterates the
 * full `previewPaths` list (primary bright, secondaries dimmed); it was simply never
 * fed more than the primary.
 */
export function createPreviewPort(
  aim: PreviewTarget,
  pathRegistry: PreviewPathSource,
): PreviewPort {
  let lastPreviewCol = -1;
  let lastPreviewRow = -1;
  return {
    update(col: number, row: number): void {
      if (col === lastPreviewCol && row === lastPreviewRow && aim.previewPaths) {
        return; // same cell — reuse the cached preview
      }
      lastPreviewCol = col;
      lastPreviewRow = row;
      const paths = pathRegistry.previewAllPixelPathsWithBlock(col, row);
      aim.previewPaths = paths && paths.length > 0 ? paths : null;
    },
    clear(): void {
      aim.previewPaths = null;
      lastPreviewCol = -1;
      lastPreviewRow = -1;
    },
  };
}

export interface PlacePortDeps {
  gs: {
    towers: Tower[];
    firewalls: Firewall[];
    pathPixels: Vec2[];
  };
  grid: Grid;
  economy: Pick<EconomySystem, "spend" | "consumeFreeTower">;
  synergySystem: Pick<SynergySystem, "setTowers" | "recalculate">;
  spawnDetectionSystem: { markDetected(col: number, row: number): void };
  waveManager: WaveManager;
  pathRegistry: PathRegistry;
  eventBus: Pick<EventBus, "emit">;
  recalculatePath: () => void;
}

/**
 * The commit port — the EXACT bodies lifted from the `click` handler's firewall-place and
 * tower/signal-place branches (incl. the free-first-tower consume and the Signal-Overload
 * paradox). Callers gate on `canBuild` (eligible && affordable && legal) before commit,
 * exactly as the old guards did, so this body assumes a validated placement.
 */
export function createPlacePort(deps: PlacePortDeps): PlacePort {
  const {
    gs,
    grid,
    economy,
    synergySystem,
    spawnDetectionSystem,
    waveManager,
    pathRegistry,
    eventBus,
    recalculatePath,
  } = deps;

  function commitFirewall(col: number, row: number): void {
    economy.spend(FIREWALL_DEFAULTS.cost);
    grid.setCell(col, row, CellType.WALL);

    const cx = col * CELL_SIZE + CELL_SIZE / 2;
    const cy = row * CELL_SIZE + CELL_SIZE / 2;
    const fw = new Firewall();
    fw.init(col, row, cx, cy, FIREWALL_DEFAULTS);
    gs.firewalls.push(fw);

    recalculatePath();
    waveManager.pathPixels = gs.pathPixels;
    // CR-H6: reroute enemies on ALL active paths (primary + breaches).
    rerouteAllPaths(waveManager, pathRegistry);

    eventBus.emit("firewall.placed", { firewall: fw });
  }

  function commitTower(desc: PlaceableDescriptor, col: number, row: number): void {
    const config = desc.config!;
    const cell = grid.getCell(col, row);
    const isSignalOnHidden =
      config.element === "signal" && cell?.type === CellType.HIDDEN_SPAWN;

    // Consume free tower AFTER placement validation passes (gated by the caller).
    const isFree = economy.consumeFreeTower();
    if (!isFree) economy.spend(config.cost);
    grid.setCell(col, row, CellType.TOWER);

    const cx = col * CELL_SIZE + CELL_SIZE / 2;
    const cy = row * CELL_SIZE + CELL_SIZE / 2;
    const tower = new Tower();
    tower.init(col, row, cx, cy, config);
    // BL-254 invariant (load-bearing — do not remove): a free-placed tower
    // must record ZERO base spend, so selling it refunds 0 for the base (only
    // real upgrade spend accrues afterward). This is the single guard that
    // keeps `free_first_tower` from becoming a gold fountain via place→sell.
    if (isFree) tower.totalInvested = 0;
    gs.towers.push(tower);

    recalculatePath();
    waveManager.pathPixels = gs.pathPixels;
    rerouteAllPaths(waveManager, pathRegistry);
    synergySystem.setTowers(gs.towers);
    synergySystem.recalculate();

    eventBus.emit("tower.placed", { tower });

    // Signal Overload paradox: tower placed on HIDDEN_SPAWN → instant feedback loop.
    if (isSignalOnHidden) {
      spawnDetectionSystem.markDetected(col, row);
      gs.towers = gs.towers.filter((t) => t !== tower);
      grid.setCell(col, row, CellType.DORMANT_SPAWN);
      recalculatePath();
      waveManager.pathPixels = gs.pathPixels;
      synergySystem.setTowers(gs.towers);
      synergySystem.recalculate();
      eventBus.emit("signal.overload", { col, row });
    }
  }

  return {
    commit(desc: PlaceableDescriptor, col: number, row: number): void {
      if (desc.kind === "firewall") commitFirewall(col, row);
      else commitTower(desc, col, row);
    },
  };
}
